import { Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import { getProceduresByPetIdForCertificate } from "../../../redux/reducers/petSlice";
import { a4PrintConfig } from "../../../util/commonConfig";
import { printFromNode, warmPrintFrame } from "../../../util/fastPrint";
import { downloadTypeList } from "../../../util/dropList";
import { genderEnum } from "../../../util/enum";
import { getRightPackageUom } from "../../../util/function";
import { getPetAgeDisplay } from "../../../util/getAge";
import ContentToPrint from "../../CommonScreens/ContentToPrint/ContentToPrint";

const initValues = {
  type: "Vaccine certificate",
  startDate: dayjs(),
  endDate: dayjs(),
};

const CerificatesDownloadModal = ({
  modVsble = false,
  onCloseMod = () => {},
}) => {
  const dispatch = useDispatch();
  const contentToPrint = useRef(null);
  const location = useLocation();
  const { appointment } = location.state;
  const [modValues, setModValues] = useState(initValues);
  const [prevModVsble, setPrevModVsble] = useState(false);
  const { certificateProcedure, petDetails } = useSelector(
    (state) => state?.pet,
  );
  const appData = useSelector((state) => state?.pet?.complaintSummary?.data);
  const apntId = appointment?.appoinment?.appointmentId;
  const petData = apntId ? appData?.pet : petDetails;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { stocks, clinic: clinicDet } = useMemo(() => {
    if (!mandateDataRaw) {
      return { stocks: [], clinic: {} };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
        stocks: Array.isArray(reqData?.stocks) ? reqData?.stocks : [],
      };
    } catch {
      return { stocks: [], clinic: {} };
    }
  }, [mandateDataRaw]);

  const onChange = (name, value) => {
    setModValues((prev) => ({ ...prev, [name]: value }));
  };

  const toDayjsDate = (dateValue) => {
    if (dateValue == null || dateValue === "") return null;

    if (typeof dateValue === "string") {
      const datePart = dateValue.split("T")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return dayjs(datePart).startOf("day");
      }
    }

    if (dayjs.isDayjs(dateValue)) {
      return dateValue.isValid() ? dateValue.startOf("day") : null;
    }

    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
      return dayjs(dateValue).startOf("day");
    }

    const parsed = dayjs(dateValue);
    return parsed.isValid() ? parsed.startOf("day") : null;
  };

  const handlePreview = () => {
    if (modValues?.type === "Vaccine certificate") {
      dispatch(
        getProceduresByPetIdForCertificate({
          petId: appointment?.appoinment?.petId,
          // url: defaultUrl,
        }),
      ).then((res) => {
        if (res?.payload) setPrevModVsble(true);
      });
    }
  };

  const getStockCategoryById = (stckId = null) => {
    if (!stckId) return null;
    const reqStock = stocks?.find((stc) => stc?.stockId === stckId);
    return (reqStock?.category ?? "")?.toLowerCase();
  };

  const getVaccineData = () => {
    const reqList = (certificateProcedure?.data ?? [])?.flatMap((procedure) =>
      (procedure?.procedureDetails ?? [])
        ?.filter((detail) => detail.type === "Administered")
        ?.map((detail) => ({
          procedureId: procedure.procedureId,
          appointmentId: procedure.appointmentId,
          serviceId: procedure.serviceId,
          procedureName: procedure.procedureName,
          category: procedure.category,
          subCategory: procedure.subCategory,
          doneAt: procedure.doneAt,
          status: procedure.status,
          doctorId: procedure.doctorId,
          doctorNamae: procedure.doctorName,
          ...detail,
        })),
    );

    const rangeStart = toDayjsDate(modValues?.startDate);
    const rangeEnd = toDayjsDate(modValues?.endDate);
    if (!rangeStart || !rangeEnd) return reqList ?? [];

    const start = rangeStart.isAfter(rangeEnd) ? rangeEnd : rangeStart;
    const end = rangeStart.isAfter(rangeEnd) ? rangeStart : rangeEnd;

    const filteredList = reqList?.filter((rl) => {
      const created = toDayjsDate(rl?.createdDate);
      if (!created) return false;
      const dateMatch =
        !created.isBefore(start, "day") && !created.isAfter(end, "day");
      if (!dateMatch || !rl?.stockId) return false;
      const stckCat = getStockCategoryById(rl?.stockId);
      const catMatch = stckCat === "vaccines";
      return dateMatch && catMatch;
    });
    return filteredList ?? [];
  };

  const onModalClose = () => {
    onCloseMod("close");
  };

  useEffect(() => {
    if (!prevModVsble || typeof window === "undefined") return;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const node = contentToPrint.current;
      if (node) warmPrintFrame(node, a4PrintConfig);
    };
    const frameId = requestAnimationFrame(warm);
    const node = contentToPrint.current;
    const imgs = node ? [...node.querySelectorAll("img")] : [];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", warm, { once: true });
        img.addEventListener("error", warm, { once: true });
      }
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      imgs.forEach((img) => {
        img.removeEventListener("load", warm);
        img.removeEventListener("error", warm);
      });
    };
  }, [prevModVsble, modValues?.type, certificateProcedure]);

  const handlePrint = () => {
    printFromNode(contentToPrint.current, {
      title: "",
      pageStyle: a4PrintConfig,
    });
  };

  const getBatch = (stockId, batchId = null, retStock = false) => {
    if (!stockId) return null;
    const reqStock = stocks?.find((stc) => stc?.stockId === stockId);
    if (retStock) return reqStock;
    if (!batchId) return null;
    return (
      reqStock?.stockBatch?.find((bat) => bat?.batchId === batchId) ?? null
    );
  };

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={onModalClose}
        header="Download"
        rightModal
        modalWidth={30}
      >
        <Grid container spacing={1} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography className="gray7 txt-semi-bold mb10">Type: </Typography>
            <CustomSelect
              select
              list={downloadTypeList}
              value={modValues?.type}
              handleChange={(e) => onChange("type", e?.target?.value)}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6} lg={6} xl={6} className="mt10">
            <CustomTextField
              name="startDate"
              label="Start Date"
              fullWidth
              value={modValues?.startDate}
              handleChange={(e) => onChange("startDate", e?.target?.value)}
              mobileDate
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6} xl={6} className="mt10">
            <CustomTextField
              name="endDate"
              label="End Date"
              fullWidth
              value={modValues?.endDate}
              handleChange={(e) => onChange("endDate", e?.target?.value)}
              mobileDate
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex1-end mt20">
              <div className="w30Per">
                <CustomButton text="Preview" smallBtn onClick={handlePreview} />
              </div>
            </div>
          </Grid>
        </Grid>
      </CustomModal>

      <CustomModal
        open={prevModVsble}
        onClose={onModalClose}
        header=""
        headerCenter
        modal
        headerBold
        modalWidth={80}
      >
        <div className="scroll-80vh">
          <ContentToPrint
            ref={contentToPrint}
            footer={
              <div className="prescription-print-footer-inner">
                <div className="mb20 flex-start prescription-print-footer-doctor-row">
                  <div>
                    <div className="txt-semi-bold fs12 black">
                      Clinician Signature
                    </div>
                  </div>
                </div>
                {clinicDet?.footer?.length > 0 ? (
                  <img src={clinicDet?.footer} alt="" />
                ) : null}
              </div>
            }
            header={
              <div className="flex-row-ali-cen mv20">
                <div className="w35Per">
                  <div>
                    {clinicDet?.image ? (
                      <img src={clinicDet?.image} className="img-h75" alt="" />
                    ) : (
                      <div className="flex-center h50img">
                        <Typography className="font-bold fs30 white-color capitalize">
                          {clinicDet?.name?.[0]}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w30Per flex-center">
                  <div className="font-bold fs20 blue-color">
                    Vaccination history
                  </div>
                </div>
                <div className="w35Per flex1-end">
                  <div className="prescription-doc-header-clinic">
                    <div className="font-bold blue-color fs16">
                      {clinicDet?.name ?? ""}
                    </div>
                    <div className="txt-regular fs12 blue-color">
                      {clinicDet?.address ?? ""}
                    </div>
                    {clinicDet?.taxId?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          TAX ID:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.taxId ?? ""}
                        </div>
                      </div>
                    ) : null}
                    {clinicDet?.mobile?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          Contact:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.mobile ?? ""}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            }
          >
            <tr>
              <td className="prescription-print-tbody-cell">
                <div className="prescription-print-body-inner">
                  <div className="p5">
                    <div>
                      <div className="flex-row prescription-print-patient-block">
                        <div className="w65Per">
                          <div className="flex-column">
                            <div className="flex-row mv5">
                              {petData?.petType?.length > 0 ? (
                                <>
                                  <div className="font-bold fs12 blue-color">
                                    Pet Name:
                                  </div>
                                  <div className="font-bold fs12 black capitalize ml5">
                                    {petData?.petName}
                                  </div>
                                </>
                              ) : null}
                              {petData?.petType?.length > 0 ? (
                                <>
                                  <div className="font-bold fs12 blue-color ml15">
                                    Species:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.petType}
                                  </div>
                                </>
                              ) : null}
                              {petData?.breed?.length > 0 ? (
                                <>
                                  <div
                                    className={`font-bold fs12 blue-color ${petData?.petType?.length > 0 ? "ml15" : ""}`}
                                  >
                                    Breed:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.breed}
                                  </div>
                                </>
                              ) : null}
                            </div>
                            <div className="flex-row mv5">
                              {genderEnum?.[petData?.gender]?.length > 0 ? (
                                <>
                                  <div className="font-bold fs12 blue-color">
                                    Gender:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {genderEnum?.[petData?.gender]}
                                  </div>
                                </>
                              ) : null}
                              {petData?.weight?.length > 0 ? (
                                <>
                                  <div
                                    className={`font-bold fs12 blue-color ${genderEnum?.[petData?.gender]?.length > 0 ? "ml15" : ""}`}
                                  >
                                    Weight:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.weight} Kg
                                  </div>
                                </>
                              ) : null}
                              {petData?.dob?.length > 0 ? (
                                <>
                                  <div
                                    className={`font-bold fs12 blue-color ${genderEnum?.[petData?.gender]?.length > 0 ? "ml15" : ""}`}
                                  >
                                    Age:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {getPetAgeDisplay(petData?.dob)}
                                  </div>
                                </>
                              ) : null}
                            </div>
                            <div className="flex-row mv5">
                              <div className="font-bold fs12 blue-color">
                                Customer:
                              </div>
                              <div className="font-bold fs12 black capitalize ml5">
                                {petData?.userName}
                              </div>
                            </div>
                            {petData?.address?.length > 0 ? (
                              <div className="flex-row mv5">
                                <div className="w40Per">
                                  <div className="font-bold fs12 blue-color">
                                    Address:
                                  </div>
                                  <div className="txt-regular fs12 black capitalize ml5">
                                    {petData?.address}
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="w35Per">
                          <div className="flex-column">
                            <div className="flex-row mv5">
                              <div className="font-bold fs12 blue-color">
                                Date:
                              </div>
                              <div className="txt-regular fs12 black ml5">
                                {`${moment(
                                  new Date(modValues?.startDate),
                                ).format("DD MMM YYYY")} -
                                  ${moment(new Date(modValues?.endDate)).format(
                                    "DD MMM YYYY",
                                  )}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="box-hor-split mv10" />
                      {getVaccineData()?.length > 0 ? (
                        <div className="prescription-print-table cert-vaccine-print-table">
                          <div className="flex-row-ali-cen ph10 cert-vaccine-print-col-head">
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Date
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Name
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Batch No
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Expiry
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Dose
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Route
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Administered By
                              </Typography>
                            </div>
                            <div className="prescription-print-cell">
                              <Typography className="fs12 txt-semi-bold black">
                                Next Due
                              </Typography>
                            </div>
                          </div>
                          <div className="blk-hor-split mb3 mt10" />
                          {getVaccineData()?.map((li, i) => {
                            const batch = getBatch(
                              li?.stockId,
                              li?.batchNumber?.value ?? li?.batchId,
                            );
                            return (
                              <div className="mv5" key={`mainLi_${i}`}>
                                <div
                                  className="flex-row ph10 mv5 cert-vaccine-print-row"
                                  key={`rec_${li?.id}`}
                                >
                                  <div className="prescription-print-cell">
                                    <Typography className="fs12 font-medium black4">
                                      {moment(li?.createdDate).format(
                                        "DD MMM YYYY",
                                      )}
                                    </Typography>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <Typography className="fs12 font-medium black4">
                                      {li?.procedureName ?? ""}
                                    </Typography>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <Typography className="fs12 font-medium black4">
                                      {li?.batchNo ?? batch?.batchNo ?? ""}
                                    </Typography>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <div className="fs12 font-medium black4">
                                      {batch?.expiryDate?.length > 0
                                        ? moment(batch?.expiryDate).format(
                                            "DD MMM YYYY",
                                          )
                                        : ""}
                                    </div>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <Typography className="fs12 font-medium black4">
                                      {li?.dosage ?? ""}
                                      {getRightPackageUom(
                                        batch?.packageUom,
                                        true,
                                      )?.length > 0
                                        ? getRightPackageUom(
                                            batch?.packageUom,
                                            true,
                                          )
                                        : ""}
                                    </Typography>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <Typography className="fs12 font-medium black4">
                                      {li?.route ?? ""}
                                    </Typography>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <div className="fs12 font-medium black4">
                                      {li?.doctorNamae ?? ""}
                                    </div>
                                  </div>
                                  <div className="prescription-print-cell">
                                    <div className="fs12 font-medium black4">
                                      {li?.nextDueDate?.length > 0
                                        ? moment(li?.nextDueDate).format(
                                            "DD MMM YYYY",
                                          )
                                        : ""}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="no-rec">No records available</div>
                      )}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ContentToPrint>
          <div className="flex1-end mv20">
            <div>
              <CustomButton text="Print" onClick={handlePrint} smallBtn />
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default CerificatesDownloadModal;
